import { create } from "zustand";
import type { NativeDeepLinkPayload } from "%/webviewBridge";

const STORAGE_KEY = "mung-pending-navigation-intent";
const STORED_INTENT_TTL_MS = 10 * 60 * 1000;

export interface NavigationIntent {
  id: string;
  target: string;
  payload: NativeDeepLinkPayload;
  createdAt: number;
}

export interface NavigatedNavigationIntent extends NavigationIntent {
  arrived: boolean;
}

interface NavigationIntentState {
  pendingIntent: NavigationIntent | null;
  navigatedIntent: NavigatedNavigationIntent | null;
  authReadyRevision: number;
  enqueueDeepLinkIntent: (
    payload: NativeDeepLinkPayload,
    target: string,
  ) => NavigationIntent;
  markIntentNavigated: (id: string) => void;
  markIntentArrived: (id: string) => void;
  clearNavigatedIntent: (id: string) => void;
  clearNavigationIntent: () => void;
  notifyAuthReady: () => void;
}

const createIntentId = (payload: NativeDeepLinkPayload): string =>
  payload.deepLinkId || `deep-link-${Date.now()}`;

const loadStoredIntent = (): NavigationIntent | null => {
  if (typeof window === "undefined") {
    return null;
  }

  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    if (!raw) {
      return null;
    }

    const intent = JSON.parse(raw) as NavigationIntent;
    if (!intent.createdAt || Date.now() - intent.createdAt > STORED_INTENT_TTL_MS) {
      window.sessionStorage.removeItem(STORAGE_KEY);
      return null;
    }
    return intent;
  } catch (error) {
    console.error("pending navigation intent 복구 실패:", error);
    window.sessionStorage.removeItem(STORAGE_KEY);
    return null;
  }
};

const saveIntent = (intent: NavigationIntent | null) => {
  if (typeof window === "undefined") {
    return;
  }

  try {
    if (!intent) {
      window.sessionStorage.removeItem(STORAGE_KEY);
      return;
    }

    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(intent));
  } catch (error) {
    console.error("pending navigation intent 저장 실패:", error);
  }
};

export const useNavigationIntentStore = create<NavigationIntentState>(
  (set, get) => ({
    pendingIntent: loadStoredIntent(),
    navigatedIntent: null,
    authReadyRevision: 0,
    enqueueDeepLinkIntent: (payload, target) => {
      const previous = get().pendingIntent;
      const id = createIntentId(payload);
      const intent =
        previous?.id === id
          ? { ...previous, payload, target }
          : {
              id,
              payload,
              target,
              createdAt: Date.now(),
            };

      saveIntent(intent);
      set({ pendingIntent: intent });
      return intent;
    },
    markIntentNavigated: (id) => {
      const current = get().pendingIntent;
      if (!current || current.id !== id) {
        return;
      }

      saveIntent(null);
      set({
        pendingIntent: null,
        navigatedIntent: { ...current, arrived: false },
      });
    },
    markIntentArrived: (id) => {
      const current = get().navigatedIntent;
      if (!current || current.id !== id || current.arrived) {
        return;
      }

      set({ navigatedIntent: { ...current, arrived: true } });
    },
    clearNavigatedIntent: (id) => {
      const current = get().navigatedIntent;
      if (!current || current.id !== id) {
        return;
      }

      set({ navigatedIntent: null });
    },
    clearNavigationIntent: () => {
      saveIntent(null);
      set({ pendingIntent: null, navigatedIntent: null });
    },
    notifyAuthReady: () => {
      set((state) => ({ authReadyRevision: state.authReadyRevision + 1 }));
    },
  }),
);

export const notifyNavigationAuthReady = () => {
  useNavigationIntentStore.getState().notifyAuthReady();
};

export const shouldDeferDefaultNavigationForIntent = (): boolean => {
  const state = useNavigationIntentStore.getState();
  const hasPendingIntent = !!state.pendingIntent;
  const hasNavigatedIntent = !!state.navigatedIntent;

  if (hasPendingIntent) {
    state.notifyAuthReady();
    return true;
  }

  if (hasNavigatedIntent) {
    return true;
  }

  return false;
};
